'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  CalendarDays,
  CloudSnow,
  Coins,
  ScrollText,
  Settings2,
  Menu,
  X,
  User,
  LogOut,
  LogIn,
  MessageCircle,
  SlidersHorizontal,
  MapPin,
  Newspaper,
  Grid3X3,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import EventSelector from './EventSelector';
import EmailSignUpModal from './EmailSignUpModal';
import ConnectionStatus from './ConnectionStatus';
import Icon from '@/components/ui/AppIcon';


const menuItems = [
  { href: '/updates', label: 'Updates', icon: Newspaper },
  { href: '/bingo', label: 'MPS-Bingo', icon: Grid3X3 },
  { href: '/weather-warning', label: 'Wetter', icon: CloudSnow },
  { href: '/venue-map', label: 'Karte & Plan', icon: MapPin },
  { href: '/met-kasse-donation', label: 'Met-Kasse', icon: Coins },
  { href: '/contact', label: 'Kontakt', icon: MessageCircle },
  { href: '/settings', label: 'Einstellungen', icon: SlidersHorizontal },
  { href: '/impressum-legal-notice', label: 'Impressum', icon: ScrollText },
  { href: '/admin-panel', label: 'Admin', icon: Settings2 },
];

export default function TopNav() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signup' | 'signin' | 'account'>('signup');
  const { user, signOut } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <>
      <header className="sticky top-0 z-50 w-full bg-card border-b border-border shadow-card">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16 gap-3">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 group flex-shrink-0">
              <AppLogo size={36} />
              <span className="font-display text-sm sm:text-base font-semibold text-primary tracking-wider leading-tight hidden sm:block">
                Mein Phantasie Spectaculum
              </span>
            </Link>

            {/* Event Selector */}
            <div className="flex-1 flex justify-center">
              <EventSelector />
            </div>

            {/* Right side: Spielplan + ConnectionStatus + Hamburger */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <ConnectionStatus />

              {/* Spielplan button — always visible */}
              <Link
                href="/"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                  pathname === '/' ? 'tab-active' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <CalendarDays size={15} />
                <span className="hidden sm:inline">Spielplan</span>
              </Link>

              {/* Hamburger menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Menü öffnen"
                >
                  {menuOpen ? <X size={22} /> : <Menu size={22} />}
                </button>

                {/* Dropdown */}
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-lg shadow-lg z-50 fade-in overflow-hidden">
                    <nav className="flex flex-col py-1">
                      {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = pathname === item.href;
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={() => setMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                              isActive
                                ? 'tab-active' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            <Icon size={16} />
                            {item.label}
                          </Link>
                        );
                      })}

                      {/* Auth section */}
                      <div className="border-t border-border mt-1 pt-1">
                        {user ? (
                          <>
                            <button
                              onClick={() => { setAuthModalMode('account'); setShowAuthModal(true); setMenuOpen(false); }}
                              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted w-full transition-colors"
                            >
                              <User size={16} />
                              <span className="truncate max-w-32">{user?.email?.split('@')?.[0]}</span>
                            </button>
                            <button
                              onClick={() => { signOut(); setMenuOpen(false); }}
                              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted w-full transition-colors"
                            >
                              <LogOut size={16} />
                              Abmelden
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => { setAuthModalMode('signup'); setShowAuthModal(true); setMenuOpen(false); }}
                            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted w-full transition-colors"
                          >
                            <LogIn size={16} />
                            Anmelden / Registrieren
                          </button>
                        )}
                      </div>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      {showAuthModal && <EmailSignUpModal onClose={() => setShowAuthModal(false)} initialMode={authModalMode} />}
    </>
  );
}